<template>
	<ui-root v-if="form != null && data != null" :part="form" :data="data" :display="display" :errs="errors" :actions="actions" :readonly="ro" ref="rootComponent"></ui-root>
</template>

<script setup lang="ts">
/*
	OpenDialog is able to display form with given error list and button list.
	It doesn't have a capability to load or save form content. Use RowObjectDialog for this.
 */
import UiRoot from "@fd/containers/UiRoot.vue";
import type {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {FormDefinition} from "@t/fd/layout/FormDefinition.ts";
import type {DialogDisplay} from "@t/ui/Dialog.ts";
import {ErrorMap} from "@fd/PartInjectKeys.ts";
import {type FormData, FormPart} from "@t/fd/FormPart.ts";
import {ref} from "vue";

const props = withDefaults(defineProps<{
	form: InterfaceRoot<FormDefinition>|null,
	display?: DialogDisplay,
	errors?: ErrorMap,
	actions?: FormPart,
	ro?: boolean,
	/**
	 * This is the primary form on the page
	 * It controls menus and subviews
	 **/
	primary?: boolean
}>(), {
	display: () => <DialogDisplay>{ mode: 'form' },
	errors: () => [],
	primary: false,
	ro: false
});

const data = defineModel<FormData|null>({ required:true });
const rootComponent = ref<InstanceType<typeof UiRoot> | null>(null);

function setDirty(dirtyFlag:boolean):void {
	if (rootComponent.value) rootComponent.value.setDirty(dirtyFlag);
}

function setReadOnly(flag:boolean|null):void {
	if (rootComponent.value) rootComponent.value.setReadOnly(flag);
}

function setViewMode(flag:boolean|null):void {
	if (rootComponent.value) rootComponent.value.setViewMode(flag);
}

function closeDialog():Promise<boolean> {
	if (rootComponent.value) return rootComponent.value.closeDialog();
	else return Promise.resolve(false);
}

function shakeDialog():Promise<boolean> {
	if (rootComponent.value) return rootComponent.value.shakeDialog();
	else return Promise.resolve(false);
}

defineExpose({
	setDirty,
	setReadOnly,
	setViewMode,
	/** Animated close of the dialog window */
	closeDialog,
	/** Animated shake of the dialog window */
	shakeDialog
});

</script>

<style></style>